// SaveV2 persistence. First load migrates from the v1 keys (which are never
// deleted); the legacy high-score key is also kept in sync both ways so the
// still-live v1 game and the /v2/ staging build share records.

import { SAVE_KEY, normalizeSave, type SaveV2 } from './schema';
import { migrateFromV1, LEGACY_KEYS } from './migrations';

export function loadSave(): SaveV2 {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    let save: SaveV2;
    if (raw !== null) {
      save = normalizeSave(JSON.parse(raw));
    } else {
      save = migrateFromV1(localStorage);
      persist(save);
    }
    // v1 may have raised the legacy high score since our last write
    const legacy = Number(localStorage.getItem(LEGACY_KEYS.highScore) ?? 0);
    if (Number.isFinite(legacy) && legacy > save.highScore) save.highScore = legacy;
    return save;
  } catch {
    return migrateSafely();
  }
}

function migrateSafely(): SaveV2 {
  try {
    return migrateFromV1(localStorage);
  } catch {
    return normalizeSave(null);
  }
}

export function persist(save: SaveV2): void {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify(save));
    // Keep v1's key in sync during the staging period
    localStorage.setItem(LEGACY_KEYS.highScore, String(save.highScore));
  } catch {
    // Private mode / storage disabled — records just don't persist
  }
}

export interface RunResult {
  score: number;
  distance: number;
  beltIndex: number;
  districtId: string;
}

/** Fold a finished run into the save; returns which records were broken. */
export function recordRun(
  save: SaveV2,
  result: RunResult,
): { newHighScore: boolean; newDistrictBest: boolean } {
  const newHighScore = result.score > save.highScore;
  if (newHighScore) save.highScore = result.score;
  if (result.distance > save.bests.distance) save.bests.distance = result.distance;
  if (result.beltIndex > save.bests.beltIndex) save.bests.beltIndex = result.beltIndex;
  const districtBest = save.bests.byDistrict[result.districtId] ?? 0;
  const newDistrictBest = result.score > districtBest;
  if (newDistrictBest) save.bests.byDistrict[result.districtId] = result.score;
  persist(save);
  return { newHighScore, newDistrictBest };
}
